import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import CannedMessage from "../api/CannedMessage";
import { toast } from "react-hot-toast";
import Input from "../components/form/Input";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../components/organisms/Layout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  darkmode,
  selectRoles,
  selectSelectedLocation,
} from "../store/authSlice";

const AddPrecannedMessage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const editMode = location.pathname.search("edit") !== -1;
  const [shortcut, setShortcut] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [accessType, setAccessType] = useState("company");
  const roles = useSelector(selectRoles);
  const selectedLocation = useSelector(selectSelectedLocation);
  const mode = useSelector(darkmode);

  const createMessage = () => {
    setLoading(true);
    setValidationErrors(null);

    CannedMessage.create({
      shortcut: shortcut,
      message: message,
      access_type: accessType,
    })
      .then(() => {
        toast.success(t("pc-messages.pre-canned-message-created"));
        navigate("/messages/canned-messages");
        setShortcut("");
        setMessage("");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong"));
          }
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  const updateMessage = () => {
    setLoading(true);
    setValidationErrors(null);

    CannedMessage.update(params.id, {
      shortcut: shortcut,
      message: message,
      access_type: accessType,
    })
      .then(() => {
        toast.success(t("pc-messages.message-updated"));
        navigate("/messages/canned-messages");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong"));
          }
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  const loadMessage = () => {
    setLoading(true);
    CannedMessage.getById(params.id)
      .then((response) => {
        setShortcut(response.data.data.shortcut);
        setMessage(response.data.data.message);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .then(() => setLoading(false));
  };

  useEffect(() => {
    if (editMode) {
      document.title = t("title.heyhire-portal-edit-message");
      loadMessage();
    } else {
      document.title = t("title.heyhire-portal-add-new-message");
    }
    selectAccessType();
  }, []);

  const selectAccessType = () => {
    let accessType = "company";
    if (roles[0] === "super-user") {
      accessType = "system";
    } else if (
      roles[0] === "regional_manager" ||
      (roles[0] === "owner" && selectedLocation?.id)
    ) {
      accessType = "location";
    } else if (roles[0] === "location_manager") {
      accessType = "location";
    } else {
      accessType = "company";
    }
    setAccessType(accessType);
  };

  return (
    <DashboardLayout >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <Link
              to="/messages/canned-messages"
              className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <i className="far fa-chevron-left text-xl" />
            </Link>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editMode
                  ? t("pc-messages.edit-pre-canned-message")
                  : t("pc-messages.add-a-pre-canned-message")}
              </h2>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              editMode ? updateMessage() : createMessage();
            }}
            className="space-y-8"
          >
            {/* Basic Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                {t("pc-messages.message-details")}
              </h3>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6">
                <div className="col-span-full">
                  <Input
                    name="shortcut"
                    label={t("common.shortcut")}
                    type="text"
                    value={shortcut}
                    placeholder={t("pc-messages.write-a-word-to-shortcut")}
                    error={
                      validationErrors && validationErrors.shortcut
                        ? validationErrors.shortcut[0]
                        : null
                    }
                    onChange={(e) => setShortcut(e.target.value)}
                    darmo={mode}
                  />
                </div>

                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-900">
                    {t("common.message")}
                  </label>
                  <div className="mt-2">
                    <ReactQuill
                      className="quill-light"
                      theme="snow"
                      value={message}
                      onChange={setMessage}
                      placeholder={t("pc-messages.write-a-pre-canned-message")}
                    />
                    {validationErrors?.message && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.message[0]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10">
              <button
                type="button"
                onClick={() => {
                  setShortcut("");
                  setMessage("");
                }}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900"
              >
                {t("common.clear")}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <i className="fad fa-circle-notch fa-spin" />
                ) : (
                  t("common.save")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddPrecannedMessage;
